import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostListener, ViewChild } from '@angular/core';
import { WIZ_EVENT_TYPE, VALUE_ENTER, VALUE_ESCAPE, WizDateUtils } from '@wizeoo/akm-core-lib';
import {
  FilterComponent,
  WizAgGrid,
  WizBasePopUp,
  WizCheckbox,
  WizDatasource,
  WizDateField,
  WizDropdown,
  WizHgroup,
  WizNumericInput,
  WizTextInput,
  WizEventManager, WizEvent
} from '@wizeoo/akm-component-lib';
import { ColDef } from 'ag-grid-community';
import { FIELD_KIND } from '@wizeoo/akm-compute-lib';

import { ViewData } from '../../../../model/view.data';
import { ComparatorVO } from '../../../../vo/common/tool/comparator.vo';
import { FreedataArgsVO } from  '../../../../vo/core/freedataargs.vo';
import { FreedataTimelineFilterItemObj, FreedataTimelineFilterObj } from '../freedata-timeline-filter.obj';

@Component({
  // popUp d'édition du grid en mode flat : gestion des emplacements de champs et rendu
  selector: 'freedata-timeline-filter-extended-pop',
  templateUrl: './freedata-timeline-filter-extended.pop.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FreedataTimelineFilterExtendedPop extends WizBasePopUp {
  @ViewChild('agGrid') agGrid: WizAgGrid<FreedataTimelineFilterItemObj>;
  @ViewChild('popFilter', { read: FilterComponent, static: false }) popFilter: FilterComponent; // zone du filtre... du filtre
  @ViewChild('gpOperator') gpOperator: WizHgroup; // zone de comparateur pour champs numériques

  @ViewChild('ddlComparator') ddlComparator: WizDropdown; // drop de choix du comparateur
  
  @ViewChild('ti') ti: WizTextInput; // zone de saisie du texte de comparaison
  @ViewChild('ni', { read: WizNumericInput, static: false }) ni: WizNumericInput;
  @ViewChild('da', { read: WizDateField, static: false }) da: WizDateField;
  @ViewChild('cb', { read: WizCheckbox, static: false }) cb: WizCheckbox;

  @ViewChild('ni2', { read: WizNumericInput, static: false }) ni2: WizNumericInput;
  @ViewChild('da2', { read: WizDateField, static: false }) da2: WizDateField;

  // validation sur enter, sortie sur Esc
  @HostListener('window:keydown', ['$event'])
  keyEvent(event: KeyboardEvent) {
    if (event.key === VALUE_ENTER) {
      this.save();
    }
    if (event.key === VALUE_ESCAPE) {
      this.doCancel();
    }
  }


  public pvo: FreedataTimelineFilterObj; // l'objet source

  private doSaveFlag: boolean = true;
  private hasChange: boolean = false; // une modif sur la collection

  private currentDatakind: string = null;
  private currentComparator: string = null;
  private currentPartOne: any = null;
  private currentPartTwo: any = null;

  columns: ColDef[] = [
    {
      headerName: '$.common.select.all.$',
      field: 'label',
      sortable: true,
      flex: 1,
      tooltipField: 'label',
    },
  ]; // Colonnes du grid

  /** la DS du grid */
  public lineDS: WizDatasource<FreedataTimelineFilterItemObj> = new WizDatasource<FreedataTimelineFilterItemObj>([])
    .setSelectionMode('multiple')
    .build();

  /** liste des comparateurs pour les champs numériques */
  public comparatorDS: WizDatasource<ComparatorVO> = new WizDatasource<ComparatorVO>([]) // fake des comparateurs
  .setSortFields([{ property: 'order', way: 'asc', numeric: true }])
  .setSelectionMode('single')
  .build();

  constructor(public elementRef: ElementRef, protected cdref: ChangeDetectorRef) {
    super();
  }

  show(): void {
    this.pvo = this.datas[0];
    this.callback = this.datas[1];

    this.currentDatakind = this.pvo.datakind;

    if (!!this.pvo.exp) {
      // console.log('filter pop filtEx', this.pfo.wcio.filtExp);
      const part: string[] = this.pvo.exp.split(ViewData.KEY_SEPARATOR);
      this.currentComparator = part[0];
      if (part.length > 1) {
        this.currentPartOne = part[1];
      }
      if (part.length === 3) {
        this.currentPartTwo = part[2];
      }
    }

    this.feedDatasources();
    this.manageExpComps();
    this.addListeners();
  }

  /**
   * Synchronisation de la bu sélectionnée
   * @param pEvent wizEvent contenant l'item sélectionné
   */
  private onSelectionChanged(pEvent: WizEvent): void {
    this.hasChange = true;
  }

  /** sur séquence de démarrage, alimentation des listes avec leurs DS */
  public feedDatasources(): void {
    this.popFilter.configure(['label'], null, null);
   
    // alimentation de la DS des comparateurs
    this.comparatorDS.setSource(ViewData.getInstance().getExtComparatorSet(this.pvo.datakind));

    if (!!this.currentComparator) {
      // console.log('filter pop filtEx', this.pfo.wcio.filtExp);
      const part: string[] = this.pvo.exp.split(ViewData.KEY_SEPARATOR);
      const comp: ComparatorVO = this.comparatorDS.getItemByKey(this.currentComparator);
      this.comparatorDS.select([comp]);
    } else {
      this.clearExp();
    }

    this.lineDS.setFilter(this.valFilterFunc.bind(this));
    this.lineDS.setSource(this.pvo.filterDS.getWorkedSource()); // la source est déjà triée
    this.lineDS.select(this.pvo.filterDS.selectedItems);
  }

  /**
   * Fonction de filtre des langues
   * @param item Elément de la liste testé
   */
  private valFilterFunc(item: FreedataTimelineFilterItemObj): boolean {
    return this.popFilter.checkVisible(item);
  }

  /**
   * Refresh du filtre language
   * @param event : Evenement de changement dans la zone de filtre
   */
  private refreshFilter(event: WizEvent = null): void {
    this.lineDS.refresh();
  }

  /** mise en place des écouteurs */
  public addListeners(): void {
    // Filtres
    WizEventManager.addListener(this, this.popFilter, WIZ_EVENT_TYPE.FILTER, this.refreshFilter);
    WizEventManager.addListener(this, this.agGrid, WIZ_EVENT_TYPE.SELECTION_CHANGED, this.innerSelectionChange);
    // modification du comparateur
    WizEventManager.addListener(this, this.gpOperator, WIZ_EVENT_TYPE.MODEL_UPDATE, this.updateExp);

  }

  /** Sauvegarde */
  public save(): void {
    this.hide();
  }

  /** annulation des modifs */
  public doCancel(): void {
    this.doSaveFlag = false;
    this.hide();
  }

  public onHide() {

    WizEventManager.removeListener(this, this.agGrid, WIZ_EVENT_TYPE.SELECTION_CHANGED);
    WizEventManager.removeListener(this, this.popFilter, WIZ_EVENT_TYPE.FILTER);
    WizEventManager.removeListener(this, this.gpOperator, WIZ_EVENT_TYPE.MODEL_UPDATE);
   
    let newExp: string = null; // contrôle de l'expression
    // TODO construction de la newExp
    if (this.comparatorDS.selectedItem) {
      const parts: string[] = [];
      parts.push(this.comparatorDS.selectedItem.code);
      const nbParams: number = this.comparatorDS.selectedItem.nbParams;
      if( nbParams > 0) { // vide ou nonvide
        switch(this.pvo.datakind) {
          case FIELD_KIND.NUMBER:
          case FIELD_KIND.INT:  
            parts.push(this.ni.wizNumber?.toString());
            if (nbParams === 2) {
              parts.push(this.ni2?.wizNumber?.toString());
            }
            break;
          case FIELD_KIND.DATE:
            parts.push(WizDateUtils.dateToString(this.da.wizValue));
            if (nbParams === 2) {
              parts.push(WizDateUtils.dateToString(this.da2.wizValue));
            }
            break;
          case FIELD_KIND.STRING:
          case FIELD_KIND.TRS_LABEL:
            parts.push(this.ti.wizValue);
            break;
          case FIELD_KIND.BOOLEAN:
            parts.push(this.cb.wizChecked ? '1': '0');
            break;         
        }
      }
      newExp = parts.join(ViewData.KEY_SEPARATOR);
    }

    if (newExp != this.pvo.exp) {
      this.hasChange = true;
    }


    if (this.doSaveFlag && this.hasChange) {
      this.pvo.filterDS.setSelectedItems(this.lineDS.selectedItems);
      
      this.pvo.exp = newExp;
      if (newExp) {
        this.pvo.comparatorCode = newExp.split(ViewData.KEY_SEPARATOR)[0];
      } else {
        this.pvo.comparatorCode = null;
      }
      
      this.callback(this.pvo);
    }
  }


  // flag d'affichage de la zone de comparateur
  public showComparator: boolean = true;

  /** affichage de l'icone de suppression de l'expression */
  public hasExp(): boolean {
    if (this.comparatorDS?.selectedItem) {
      return true;
    }
    return false;
  }

  /** retourne le nombre de décimales selon le format du champ filtré */
  private getDecimal(arg: FreedataArgsVO): number {
    let result: number = 3;
    return result;
  }



  /** gestion des champs visibles pour l'expression */
  public manageExpComps(): void {
    this.hideExpComponent();
    if (this.currentDatakind && this.comparatorDS?.selectedItem) {    
      const nbParams: number = this.comparatorDS.selectedItem.nbParams;   
      if( nbParams > 0) { // vide ou nonvide
        switch(this.pvo.datakind) {
          case FIELD_KIND.NUMBER:
            this.ni.visible = true;
            this.ni.nbDecimal = this.getDecimal(this.pvo.arg);
            // console.log('pfo', this);
            this.ni.wizNumber = Number(this.currentPartOne);
            if (nbParams === 2) {
              this.ni2.visible = true;
              this.ni2.nbDecimal = this.getDecimal(this.pvo.arg);
              this.ni2.wizNumber = Number(this.currentPartTwo);
            }
            break;
            case FIELD_KIND.INT:
              this.ni.visible = true;
              this.ni.nbDecimal = 0;
              this.ni.wizNumber = Number(this.currentPartOne);
              if (nbParams === 2) {
                this.ni2.visible = true;
                this.ni2.nbDecimal = 0;
                this.ni2.wizNumber = Number(this.currentPartTwo);
              }
              break;
          case FIELD_KIND.DATE:
            this.da.visible = true;
            this.da.wizValue = WizDateUtils.stringToDate(this.currentPartOne);
            if (nbParams === 2) {
              this.da2.visible = true;
              this.da2.wizValue = WizDateUtils.stringToDate(this.currentPartTwo);
            }
            break;
          case FIELD_KIND.STRING:
          case FIELD_KIND.TRS_LABEL:
            this.ti.visible = true;
            this.ti.wizValue = this.currentPartOne;
            break;
          case FIELD_KIND.BOOLEAN:
            this.cb.visible = true;
            this.cb.wizChecked = !!this.currentPartOne;
            break;         
        }
      }
    }
  }

  /** masquer tous les champs d'expression */
  public hideExpComponent(): void {
    this.ti.visible = false;
    this.ni.visible = false;
    this.da.visible = false;
    this.cb.visible = false;
    this.ni2.visible = false;
    this.da2.visible = false;
  }


  /**
   * suppression de l'expression
   */
  public clearExp(): void {
    this.comparatorDS.clearSelection();
    this.hideExpComponent();
  }

  /**
   * Synchronisation du selected
   * @param pEvent wizEvent contenant l'item sélectionné
   */
  private innerSelectionChange(pEvent: WizEvent): void {
    this.hasChange = true;
  }

  public updateExp(e: WizEvent): void {
    // if (this.lineDS.selectedItems.length > 0) {
    //   this.lineDS.clearSelection();
    // }
    this.hasChange = true;
    // console.log('updateExp', e.currentTarget);
    switch(e.component) {
      case this.ddlComparator:
        this.manageExpComps();
        break;
    }

  }

}
